import type { CoreCanvasView, TileSize, ViewSpec } from '../../types';

// --- Spec-derived thumbnail descriptors (SC-2873) ------------------------------
// Pure derivation, kept out of the component file (react-refresh only allows
// component exports there). A library row's thumbnail is a wireframe of the
// view's tile grid, derived FROM the spec itself — no screenshots, no
// staleness. Core views get hand-authored descriptors until they're
// spec-rendered (the SC-2568 Stage-1 endgame).

export type ThumbKind = 'metric' | 'bar' | 'donut' | 'table' | 'plain';

export interface ThumbDesc {
  size: TileSize;
  kind: ThumbKind;
}

/** Derive thumbnail descriptors from a board spec's tile grid. */
export function specThumbDescs(spec: ViewSpec): ThumbDesc[] {
  const tiles = Array.isArray(spec.view?.tiles) ? spec.view.tiles : [];
  return tiles.map((tile) => {
    const node = tile.node as { component?: string; kind?: string };
    const kind: ThumbKind =
      node.component === 'Metric'
        ? 'metric'
        : node.component === 'Chart'
          ? node.kind === 'donut'
            ? 'donut'
            : 'bar'
          : node.component === 'Table' || node.component === 'List'
            ? 'table'
            : node.component === 'Section'
              ? 'bar'
              : 'plain';
    return { size: tile.size, kind };
  });
}

/** Hand-authored skeletons for the core views (not yet spec-rendered). The
 *  entities view's is used for the anchor row whatever the entity term is. */
export const CORE_THUMB_DESCS: Record<CoreCanvasView, ThumbDesc[]> = {
  entities: [
    { size: 'full', kind: 'table' },
    { size: 'full', kind: 'table' },
  ],
  attributes: [
    { size: 'full', kind: 'table' },
    { size: 'full', kind: 'table' },
  ],
  findings: [
    { size: 'compact', kind: 'plain' },
    { size: 'half', kind: 'table' },
    { size: 'compact', kind: 'plain' },
    { size: 'half', kind: 'table' },
  ],
  history: [
    { size: 'compact', kind: 'plain' },
    { size: 'full', kind: 'table' },
  ],
  profile: [
    { size: 'half', kind: 'plain' },
    { size: 'half', kind: 'plain' },
    { size: 'full', kind: 'plain' },
  ],
};
